import { createApi } from '@reduxjs/toolkit/query/react';

import {
  baseQuery,
  PaginableList,
  PaginableResponse,
} from 'src/services/redux/api';
import { User, userListTag } from 'src/services/user';
import { PaginationMeta } from '../video/types';
import {
  CreateFirmRequest,
  Firm,
  FirmListRequest,
  FirmRequest,
  FirmUserListRequest,
  UpdateFirmRequest,
  UpdateFirmSsoRequest,
} from './types';

export const FIRM_TAG_TYPE = 'Firm' as string;
export const firmListTag = { type: FIRM_TAG_TYPE, id: 'FirmList' };

export const FIRM_USER_TAG_TYPE = 'FirmUser' as string;
export const firmUserListTag = { type: FIRM_USER_TAG_TYPE, id: 'FirmUserList' };

export const firmApi = createApi({
  baseQuery,
  endpoints: (builder) => ({
    createFirm: builder.mutation<void, CreateFirmRequest>({
      invalidatesTags: [firmListTag],
      query: (body) => ({
        body,
        method: 'POST',
        url: 'firm',
      }),
    }),
    updateFirm: builder.mutation<void, UpdateFirmRequest>({
      invalidatesTags: [firmListTag],
      query: (body) => ({
        body,
        method: 'PUT',
        url: 'firm',
      }),
    }),
    firm: builder.query<Firm, FirmRequest>({
      providesTags: (res) => [{ id: res?.id, type: FIRM_TAG_TYPE }],
      transformResponse: (res: { data: Firm }) => res?.data,
      query: ({ id }) => ({
        method: 'GET',
        url: `firm/${id}`,
      }),
    }),
    firmList: builder.query<
      { items: Firm[]; meta: PaginationMeta },
      FirmListRequest | void
    >({
      providesTags: (res) =>
        res
          ? [
              ...res.items.map(({ id }) => ({
                type: FIRM_TAG_TYPE,
                id,
              })),
              firmListTag,
            ]
          : [firmListTag],
      transformResponse: (res: {
        data: { items: Firm[]; meta: PaginationMeta };
      }) => res?.data,
      query: (params: FirmListRequest) => ({
        params: params,
        method: 'GET',
        url: 'firm',
      }),
    }),
    firmUserList: builder.query<PaginableList<User>, FirmUserListRequest>({
      providesTags: (res) =>
        res
          ? [
              ...res.items.map(({ id }) => ({
                type: FIRM_USER_TAG_TYPE,
                id,
              })),
              firmUserListTag,
            ]
          : [firmUserListTag],
      transformResponse: (res: PaginableResponse<User>) => res.data,
      query: ({ id, ...params }) => ({
        method: 'GET',
        params,
        url: `firm/${id}/users`,
      }),
    }),
    updateFirmSso: builder.mutation<void, UpdateFirmSsoRequest>({
      invalidatesTags: (_, __, { firmId }) => [{ type: FIRM_TAG_TYPE, id: firmId }],
      query: (body) => ({
        body,
        method: 'PUT',
        url: 'firm',
      }),
    }),
    toggleFirmActivation: builder.mutation<void, { id: number }>({
      invalidatesTags: [firmListTag, firmUserListTag, userListTag],
      query: ({ id }) => ({
        method: 'PATCH',
        url: `firm/${id}/toggle-activation`,
      }),
    }),
    importFirmUsers: builder.mutation<
      { success: boolean; message: string; data?: { imported: number; errors: { row: number; message: string }[] } },
      { rows: { firstName: string; lastName: string; email: string; role: string; firmName?: string }[] }
    >({
      invalidatesTags: [firmUserListTag, userListTag],
      transformResponse: (res: { success: boolean; message: string; data?: { imported: number; errors: { row: number; message: string }[] } }) => res,
      query: ({ rows }) => ({
        body: { rows },
        method: 'POST',
        url: `firm/import/users`,
      }),
    }),
    deleteFirm: builder.mutation<void, { id: number }>({
      invalidatesTags: [firmListTag, firmUserListTag],
      query: ({ id }) => ({
        method: 'DELETE',
        url: `firm/${id}`,
      }),
    }),
  }),
  reducerPath: 'firmApi',
  tagTypes: [FIRM_TAG_TYPE, FIRM_USER_TAG_TYPE],
});

export const {
  useCreateFirmMutation,
  useDeleteFirmMutation,
  useFirmListQuery,
  useFirmQuery,
  useFirmUserListQuery,
  useImportFirmUsersMutation,
  useLazyFirmListQuery,
  useLazyFirmUserListQuery,
  useToggleFirmActivationMutation,
  useUpdateFirmMutation,
  useUpdateFirmSsoMutation,
} = firmApi;
